import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Modal,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { formatCcy } from '../../../utils/helpers';
import { translatePaymentType, translatePaymentMethod } from '../types';
import useStyles from './styles';

type ViewPaymentModalProps = {
  open: boolean;
  onClose: () => void;
  paymentData: {
    date: string;
    amount: number;
    studentName: string;
    studentLastname: string;
    studentRut: string;
    type: 'tuition' | 'fee';
    paymentMethod: string;
    comment: string;
    imageUrl: string;
  } | null;
};

function ViewPaymentModal({
  open,
  onClose,
  paymentData,
}: ViewPaymentModalProps) {
  const classes = useStyles();
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (open && paymentData?.imageUrl) {
      setImageLoading(true);
    }
  }, [open, paymentData?.imageUrl]);

  if (!paymentData) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const isPdfFile = (url: string): boolean => {
    try {
      const lowerUrl = url.toLowerCase();
      const urlWithoutQuery = lowerUrl.split('?')[0];
      return urlWithoutQuery.endsWith('.pdf') || lowerUrl.includes('application/pdf');
    } catch {
      return url.toLowerCase().includes('.pdf');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modalBackdrop}
    >
      <Card className={classes.card}>
        <CardContent className={classes.content}>
          <Box className={classes.header}>
            <Typography className={classes.title}>
              Comprobante de pago
            </Typography>
            <IconButton onClick={onClose} className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className={classes.infoSection}>
            <Box className={classes.infoItem}>
              <Typography className={classes.label}>Nombre alumno:</Typography>
              <Typography className={classes.value}>
                {paymentData.studentName} {paymentData.studentLastname}
              </Typography>
            </Box>
            <Box className={classes.infoItem}>
              <Typography className={classes.label}>Fecha:</Typography>
              <Typography className={classes.value}>
                {formatDate(paymentData.date)}
              </Typography>
            </Box>
            <Box className={classes.infoItem}>
              <Typography className={classes.label}>Monto:</Typography>
              <Typography className={classes.value}>
                {formatCcy(paymentData.amount, '.')}
              </Typography>
            </Box>
            <Box className={classes.infoItem}>
              <Typography className={classes.label}>Tipo:</Typography>
              <Typography className={classes.value}>
                {translatePaymentType(paymentData.type)}
              </Typography>
            </Box>
            <Box className={classes.infoItem}>
              <Typography className={classes.label}>Medio de Pago:</Typography>
              <Typography className={classes.value}>
                {translatePaymentMethod(paymentData.paymentMethod || '-')}
              </Typography>
            </Box>
          </Box>

          <Box className={classes.mainSection}>
            <Box className={classes.imageWrapper}>
              <Box className={classes.imageContainer}>
                {!paymentData.imageUrl && (
                  <Typography className={classes.noImageMessage}>
                    No hay comprobante disponible
                  </Typography>
                )}
                {paymentData.imageUrl && isPdfFile(paymentData.imageUrl) && (
                  <>
                    {imageLoading && (
                      <Box className={classes.imageLoading}>
                        <CircularProgress />
                      </Box>
                    )}
                    <iframe
                      src={paymentData.imageUrl}
                      title="Comprobante de pago PDF"
                      className={classes.image}
                      style={{
                        width: '100%',
                        height: '600px',
                        border: 'none',
                        display: imageLoading ? 'none' : 'block',
                      }}
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageLoading(false);
                        console.error('Error al cargar el PDF:', paymentData.imageUrl);
                      }}
                    />
                  </>
                )}
                {paymentData.imageUrl && !isPdfFile(paymentData.imageUrl) && (
                  <>
                    {imageLoading && (
                      <Box className={classes.imageLoading}>
                        <CircularProgress />
                      </Box>
                    )}
                    <img
                      src={paymentData.imageUrl}
                      alt="Comprobante de pago"
                      className={classes.image}
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageLoading(false);
                        console.error('Error al cargar la imagen:', paymentData.imageUrl);
                      }}
                      style={{ display: imageLoading ? 'none' : 'block' }}
                    />
                  </>
                )}
              </Box>
            </Box>
            <Box className={classes.commentsWrapper}>
              <Box className={classes.commentsSection}>
                <Typography className={classes.commentsTitle}>
                  Comentarios
                </Typography>
                <Divider />
                <Box className={classes.commentBox}>
                  <Box>
                    <Typography className={classes.commentLabel}>
                      Fecha de edición
                    </Typography>
                    <Typography className={classes.commentValue}>
                      {formatDate(paymentData.date)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography className={classes.commentLabel}>
                      Comentario:
                    </Typography>
                    <Typography className={classes.commentText}>
                      {paymentData.comment || 'Sin comentarios'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );
}

export default ViewPaymentModal;
